import {PlatformType, PlatformTypeType} from 'constants/platformType';

// IPC 메시지 타입 정의
interface PlatformMessage {
    type: 'GET_PLATFORM';
}

// IPC 통신을 위한 유틸리티
let cachedPlatform: PlatformTypeType | null = null;
let isPlatformRequested = false;

// 플랫폼 메시지 핸들러 등록 함수 (외부에서 호출)
export const initializePlatformMessageHandler = () => {
    if (window.ReactNativeWebView && !window.platformMessageListenerRegistered) {
        const handlePlatformMessage = (event: MessageEvent) => {
            try {
                const data = JSON.parse(event.data);

                if (data.type === 'GET_PLATFORM_CALLBACK') {
                    cachedPlatform = data.platform;
                }
            } catch (error) {}
        };

        window.addEventListener('message', handlePlatformMessage);
        window.platformMessageListenerRegistered = true;
    }
};

// 앱 시작 시 플랫폼 메시지 핸들러 초기화
// if (typeof window !== 'undefined') {
//   initializePlatformMessageHandler();
// }

//네이티브 앱에서 플랫폼 정보를 요청하는 함수
const requestPlatformFromNative = (): Promise<PlatformTypeType> => {
    return new Promise(resolve => {
        if (window.ReactNativeWebView) {
            const message: PlatformMessage = {
                type: 'GET_PLATFORM',
            };

            window.ReactNativeWebView.postMessage(JSON.stringify(message));

            // index.tsx에서 메시지를 처리하므로 즉시 web 반환
            resolve(PlatformType.WEB);
        } else {
            resolve(PlatformType.WEB);
        }
    });
};

//플랫폼 정보를 가져오는 함수 (캐싱 포함)
export const getPlatform = async (): Promise<PlatformTypeType> => {
    if (cachedPlatform !== null) {
        return cachedPlatform;
    }

    if (!isPlatformRequested) {
        isPlatformRequested = true;
        cachedPlatform = await requestPlatformFromNative();
    }

    return cachedPlatform || PlatformType.WEB;
};

//동기적으로 플랫폼 정보를 가져오는 함수 (캐시된 값 사용)
export const getPlatformSync = (): PlatformTypeType => {
    // 전역 변수에서 cachedPlatform 확인
    if (window.platformCachedPlatform) {
        cachedPlatform = window.platformCachedPlatform as PlatformTypeType;
    }

    return cachedPlatform || PlatformType.WEB;
};

// 기존 userAgent 기반 판별 함수들 (fallback용)
const isIOSUserAgent = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const isAndroidUserAgent = () => /android/i.test(navigator.userAgent);

// iOS 기기 여부 (IPC 우선, fallback으로 UserAgent 사용)
export const isIOS = (): boolean => {
    const platform = getPlatformSync();
    if (platform === PlatformType.IOS) return true;
    if (platform === PlatformType.WEB) return isIOSUserAgent();
    return false;
};

// Android 기기 여부 (IPC 우선, fallback으로 UserAgent 사용)
export const isAndroid = (): boolean => {
    const platform = getPlatformSync();
    if (platform === PlatformType.ANDROID) return true;
    if (platform === PlatformType.WEB) return isAndroidUserAgent();
    return false;
};

// 모바일 기기 여부 (iOS + Android)
export const isMobile = (): boolean => isIOS() || isAndroid();

// 네이티브 앱 웹뷰 여부
export const isAppWebView = (): boolean => {
    const platform = getPlatformSync();
    return platform === PlatformType.IOS || platform === PlatformType.ANDROID;
};

// iOS 앱 여부
export const isIOSApp = (): boolean => {
    const platform = getPlatformSync();
    return platform === PlatformType.IOS;
};

// Android 앱 여부
export const isAndroidApp = (): boolean => {
    const platform = getPlatformSync();

    return platform === PlatformType.ANDROID;
};

// 웹 브라우저 여부
export const isWebBrowser = (): boolean => {
    const platform = getPlatformSync();
    return platform === PlatformType.WEB;
};

// payment 결제 후 리다이렉트 URL에서 주문번호를 추출하는 함수
export const extractOrderIdFromUrl = (url?: string): string | null => {
    const targetUrl = url || window.location.href;

    // paymentId 파라미터에서 주문번호 추출
    const urlParams = new URLSearchParams(new URL(targetUrl).search);
    const paymentId = urlParams.get('paymentId');

    if (paymentId) {
        // dasee-xxx-숫자 형태에서 숫자 부분 추출
        const match = paymentId.match(/dasee-[^-]+-(\d+)/);
        if (match && match[1]) {
            return match[1];
        }
    }

    return null;
};

// 플랫폼 초기화 함수 (앱 시작 시 호출)
export const initializePlatform = async (): Promise<void> => {
    await getPlatform();
};
